/* eslint-disable prefer-destructuring */
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import dynamic from 'next/dynamic';
import Head from 'next/head';

// Utils
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useFormik } from 'formik';
import { isArray, isEqual, sortBy } from 'lodash';
import { Oval } from 'react-loader-spinner';
import { useAppContext } from '../../../src/store/AppContext';
import { useModal } from '../../../src/store/ModalContext';
import parseSearchQuery from '../../../src/utils/search/parseSearchQuery';
import packageUtils from '../../../src/utils/package/packageUtils';
import useReplaceCartFromUrl from '../../../src/hooks/useReplaceCartFromUrl';

// Images
import Tennis from '../../../public/images/tennis.svg';

// Hooks
import useStorage from '../../../src/hooks/useStorage';
import useFilters from '../../../src/hooks/useFilters';
import usePrevious from '../../../src/hooks/usePrevious';
import useDataLayer from '../../../src/hooks/useDataLayer';
import useCart from '../../../src/hooks/useCart';
import useWeekId from '../../../src/hooks/useWeekId';
import useLocalizedDate from '../../../src/hooks/useLocalizedDate';

// Components
import PackageCard from '../../../components/organisms/SearchResults/PackageCard/PackageCard';
import Filters from '../../../components/organisms/Filters/Filters';
import MultiPlayersModal from '../../../components/book/MultiPlayersModal/MultiPlayersModal';
import Modal from '../../../components/organisms/Modal/Modal';
import Weeks from '../../../components/organisms/SearchBar/Weeks/Weeks';
import MoreFilters from '../../../components/organisms/Filters/MoreFilters/MoreFilters';
import ReplaceCartModal from '../../../components/book/ReplaceCartModal/ReplaceCartModal';

// Services
import packagesService from '../../../src/services/packagesService';
import offersService from '../../../src/services/offersService';
import themesService from '../../../src/services/themesService';
import weeksService from '../../../src/services/weeksService';
import sportsService from '../../../src/services/sportsService';
import tagsService from '../../../src/services/tagsService';
import extrasService from '../../../src/services/extrasService';
import eventsService from '../../../src/services/eventsService';

// Enum
import packagesTypes from '../../../src/enum/packagesTypes';
import activePackageFilters from '../../../src/enum/activePackageFilters';

import styles from '../../../styles/pages/Stage.module.scss';

const BookingTunnel = dynamic(() => import('../../../components/layout/BookingTunnel/BookingTunnel'), { ssr: false });

function Stage({
  packages, formats, themes, offers, weeks, rawPackages, sports, tags, camps, allExtras, allEvents,
}) {
  const router = useRouter();
  const { t } = useTranslation(['common']);

  const eventFinded = allEvents?.find((event) => event.id === Number(router.query.eventId));

  const { setItem, getItem } = useStorage();
  const { gtmEventCheckoutStep } = useDataLayer();
  const { getPlayerLabel, getPlayerIndex } = useCart();
  const { getOfferStartEndDates } = useWeekId();
  const { getDayMonthDate } = useLocalizedDate();

  const [{
    currentPlayer, players, search, dataLayerDone,
  }, stateDispatch] = useAppContext();
  const { openModal, isModalOpen, closeModal } = useModal();

  const [firstSelectionDone, setFirstSelectionDone] = useState(false);

  const { getFilteredList } = useFilters();
  const [filters, setFilters] = useState(null);

  const [filteredPackages, setFilteredPackages] = useState(null);
  const prevList = usePrevious(filteredPackages);
  const [listWasUpdated, setListWasUpdated] = useState(0);

  const [topPackageOnInit, setTopPackageOnInit] = useState(null);

  const { handleReplaceCartFromUrl } = useReplaceCartFromUrl();

  const isWeekendTheme = filters?.themesIds?.length > 0 && filters?.themesIds?.every(
    (themeId) => {
      const matchingTheme = themes?.find((theme) => theme.id === themeId);
      return matchingTheme && matchingTheme?.durationId === 2;
    },
  );

  const weekSelectForm = useFormik({
    initialValues: {
      weeks: [],
      weekend: isWeekendTheme,
    },
    enableReinitialize: true,
  });

  const handleMultiPlayersModal = () => {
    if (players.length > 1 && !getItem('multiplayersModalSeen')) {
      openModal('modal-multiplayers');
      setItem('multiplayersModalSeen', true);
    }
  };

  const getPlayersList = () => {
    let playersList = [];

    playersList = packagesTypes.reduce(
      (list, type) => list.concat(
        Array.from({ length: search[type.label] })?.map(() => ({
          typeId: type.id,
          typeLabel: type.label,
        })),
      ),
      [],
    );

    playersList = playersList.map((player, index) => ({
      id: index + 1,
      completed: false,
      stage: null,
      ...player,
    }));
    return playersList;
  };

  useEffect(() => {
    // init players and filters from search query
    if (search && Object.values(search).length && !players) {
      // build players list
      stateDispatch({ type: 'SET_PLAYERS', payload: getPlayersList() });

      // default current player is first uncompleted player in the list
      let defaultCurrentPlayer = null;
      if (getPlayersList()?.length && !currentPlayer) {
        defaultCurrentPlayer = getPlayersList().find((pl) => !pl.completed);
        if (!defaultCurrentPlayer) defaultCurrentPlayer = getPlayersList()[0];
        stateDispatch({
          type: 'SET_CURRENT_PLAYER',
          payload: defaultCurrentPlayer || null,
        });
      }
    }
  }, [search]);

  useEffect(() => {
    if (!firstSelectionDone) setTopPackageOnInit(currentPlayer?.stage?.packageId);

    if (players?.length && currentPlayer && !filters) {
      // init filters
      setFilters(() => ({
        durationId: search.weekend ? 2 : 1,
        formatId: null,
        sportIds: [],
        tagIds: [],
        themesIds: [],
        typeId: currentPlayer.typeId,
        weekIds: isArray(search.weeks) ? search.weeks : [search.weeks],
      }));
    }

    if (players) handleMultiPlayersModal();
  }, [players, currentPlayer]);

  useEffect(() => {
    // apply filters on the packages list
    if (filters && players?.length) {
      const newList = getFilteredList(
        packages,
        currentPlayer?.typeId ? { ...filters, typeId: currentPlayer?.typeId } : filters,
        themes,
        rawPackages,
        offers,
        topPackageOnInit || null,
        currentPlayer?.id,
        players?.find((p) => p.id === 1),
      );
      if (!isEqual(newList, filteredPackages)) {
        setFilteredPackages(newList);
      }

      stateDispatch({
        type: 'SET_CONTINUE_SKIP_PLAYER',
        payload: newList?.length === 0 && players?.length > 1,
      });
    }
  }, [filters, currentPlayer]);

  useEffect(() => {
    if (prevList && !isEqual(filteredPackages, prevList)) {
      setListWasUpdated((prev) => prev + 1);

      if (filteredPackages?.length && (dataLayerDone && !dataLayerDone.checkoutStep)) {
        // gtm event on results load
        gtmEventCheckoutStep(filteredPackages, camps, formats);
      }
    }
  }, [filteredPackages]);

  const getTunnelTitle = () => {
    const base = t('booking.choice.stage.title');
    if (currentPlayer) {
      const playerLabel = getPlayerLabel({
        typeLabel: currentPlayer.typeLabel,
        typeId: currentPlayer.typeId,
        playerId: currentPlayer.id,
      });
      if (playerLabel) {
        return `${base} (${playerLabel?.toLowerCase()})`;
      }
    }

    return base;
  };

  const stayPeriods = useMemo(() => {
    const searchWeekIds = isArray(search?.weeks) ? search.weeks : (search?.weeks ? [search.weeks] : []);
    const weekIds = players?.[0]?.stage?.weeks || searchWeekIds || [];

    return weekIds?.map((weekId) => (
      getOfferStartEndDates({
        weekId,
        isWeekend: players?.[0]?.stage?.durationId === 2 || search.weekend,
        days: players?.[0]?.stage?.durationInDays || (search.weekend ? 2 : 5),
      })
    )) || [];
  }, [players, search]);

  const areAllPackagesSoldout = useCallback(() => (
    filteredPackages?.every((pack) => pack.offers?.every((offer) => offer.flag === 'SOLD_OUT'))
  ), [filteredPackages]);

  const getEmptyListText = (transKey) => (
    t(
      transKey,
      {
        start: getDayMonthDate(stayPeriods[0].start),
        end: getDayMonthDate(stayPeriods[0].end),
        morePeriods: stayPeriods?.length > 1 ? (
          stayPeriods
            .slice(1)
            .map((period, index, list) => {
              const isLast = list.length - 1 === index;

              return t(`booking.list.noResult.morePeriods.${isLast ? 'last' : 'comma'}`, {
                start: getDayMonthDate(period.start),
                end: getDayMonthDate(period.end),
              });
            })
            .join('')
        ) : '',
        player: t(`booking.list.noResult.${currentPlayer.typeLabel}`, {
          index: getPlayerIndex({
            typeId: currentPlayer.typeId,
            playerId: currentPlayer.id,
          }),
        }),
      },
    )
  );

  const handleOpenCalendar = () => openModal('modal-calendar');

  useEffect(() => {
    const openCalendarButton = document.querySelector('#openCalendarButton');
    if (openCalendarButton) {
      openCalendarButton.addEventListener('click', handleOpenCalendar);
    }
  }, [stayPeriods, filteredPackages]);

  const areFiltersDisabled = useCallback(() => {
    const { sportIds, tagIds, formatId } = activePackageFilters;

    // if one of the availables filters is used, don't disable filters
    if (filters?.[sportIds]?.length || filters?.[tagIds]?.length || filters?.[formatId]) {
      return false;
    }

    // if no results dans not first player, disable filters
    return !filteredPackages?.length;
  }, [filters, filteredPackages, currentPlayer]);

  const displayedPackages = useMemo(() => (
    sortBy(filteredPackages || [], 'ordinal').reverse().slice(0, 10)
  ), [filteredPackages]);

  useEffect(() => {
    handleReplaceCartFromUrl();
  }, []);

  return (
    <>
      <Head>
        <title>
          {t('booking.choice.stage.title')}
        </title>
      </Head>
      <BookingTunnel
        title={getTunnelTitle()}
        event={eventFinded || null}
        hideMobileTitle
        isStagePage
        mobileNoPadding
      >
        <div className={`${styles.filters} m-b-20 p-b-20`}>
          {filters ? (
            <Filters
              filters={filters}
              setFilters={setFilters}
              themes={themes}
              moreFilters={{ sports, formats, tags }}
              isDisabled={areFiltersDisabled()}
              weekSelectForm={{
                values: weekSelectForm.values,
                setFieldValue: weekSelectForm.setFieldValue,
              }}
              isWeekendTheme={isWeekendTheme}
            />
          ) : null}
        </div>
        <div className={`${styles.main_container} mobile-p-20`}>
          {currentPlayer?.id === 1
            && filteredPackages?.length
            && stayPeriods?.length
            && areAllPackagesSoldout() ? (
              <div className="p-t-10 p-b-30">
                <p
                  dangerouslySetInnerHTML={{
                    // eslint-disable-next-line max-len
                    __html: `${getEmptyListText('booking.list.noResult.soldOut')} <button id="openCalendarButton" type="button" class="${styles.open_calendar}">${t('booking.list.noResult.changeDates')}</button>`,
                  }}
                />
              </div>
            ) : null}

          {!filteredPackages ? (
            <div className={styles.loader}>
              <Oval
                isLoading
                height={50}
                width={50}
                strokeWidth={3}
                color="#191a3c"
                secondaryColor="#6875B8"
                ariaLabel="loading"
              />
            </div>
          ) : (
            filteredPackages.length ? (
              <ul>
                {displayedPackages.map((pack) => (
                  <li key={pack.id}>
                    <PackageCard
                      pack={pack}
                      formats={formats}
                      listWasUpdated={listWasUpdated}
                      firstSelectionDone={firstSelectionDone}
                      setFirstSelectionDone={setFirstSelectionDone}
                      allExtras={allExtras}
                      camps={camps}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              // --- EMPTY LIST ---
              currentPlayer && players?.[0] ? (
                currentPlayer.id === 1 ? (
                  <p className="p-t-10 p-b-40">
                    {t('booking.list.noResult.firstPlayer')}
                  </p>
                ) : (
                  stayPeriods?.length ? (
                    <div className="flex d-col a-center p-b-60">
                      <p
                        className="p-t-10 p-b-40 flex-auto"
                        dangerouslySetInnerHTML={{
                          __html: getEmptyListText('booking.list.noResult.text'),
                        }}
                      />

                      <Tennis className={styles.empty_img} />
                    </div>
                  ) : null
                )
              ) : null
            )
          )}
        </div>
      </BookingTunnel>
      <MultiPlayersModal />

      <Modal
        id="modal-calendar"
        mobileButton={t('common.apply')}
        title={t('camp.details.description.dates')}
        isMobileFullScreen
        isOpen={isModalOpen('modal-calendar')}
        onClose={() => closeModal('modal-calendar')}
      >
        {filters ? (
          <Weeks
            offers={offers}
            weeks={weeks}
            packages={rawPackages}
            formik={weekSelectForm}
            infoMessage={t('booking.choice.periods.warning')}
          />
        ) : null}
      </Modal>

      <Modal
        id="modal-more-filters"
        mobileButton={t('common.apply')}
        title={t('common.filters')}
        isMobileFullScreen
        isOpen={isModalOpen('modal-more-filters')}
        onClose={() => closeModal('modal-more-filters')}
      >
        {filters ? (
          <MoreFilters
            filters={filters}
            setFilters={setFilters}
            moreFilters={{ sports, formats, tags }}
          />
        ) : null}
      </Modal>

      <ReplaceCartModal />
    </>
  );
}

export async function getServerSideProps({ query, locale }) {
  // fetch data
  const packages = await packagesService.getPackages();
  const packagesData = packages ? packages.data : [];
  const offers = await offersService.getOffers();
  const offersData = offers ? offers.data : [];
  const weeks = await weeksService.getWeeks();
  const weeksData = weeks ? weeks.data : [];

  // filter data with query to get packages list from user weeks selection
  const parsedQuery = parseSearchQuery(query) || {};
  const selectedWeeks = isArray(parsedQuery.weeks) ? parsedQuery.weeks : [parsedQuery.weeks];

  const packagesList = packageUtils.getPackagesFromUserWeeksSelection(
    packagesData?.packages,
    offersData?.offers,
    selectedWeeks,
    parsedQuery.weekend,
  );

  // more datas
  const formats = await packagesService.getFormats();
  const formatsData = formats ? formats.data : [];

  const themes = await themesService.getThemes();
  const themesData = themes ? themes.data : [];
  const sports = await sportsService.getSports();
  const sportsData = sports ? sports.data : [];
  const tags = await tagsService.getTags();
  const tagsData = tags ? tags.data : [];

  const camps = await packagesService.getCamps();
  const campsData = camps ? camps.data : [];

  const allExtras = await extrasService.getExtras();
  const allExtrasData = allExtras ? allExtras.data : [];

  const events = await eventsService.getEvents();
  const allEventsData = events ? events.data : [];

  return {
    props: {
      ...(await serverSideTranslations(
        locale || 'fr',
        ['common'],
      )),
      packages: packagesList,
      formats: formatsData?.formats || [],
      themes: themesData?.themes || [],
      offers: offersData?.offers ? offersData.offers : [],
      weeks: weeksData?.weeks ? weeksData.weeks : {},
      rawPackages: packagesData?.packages ? packagesData.packages : [],
      sports: sportsData?.sports || [],
      tags: tagsData?.tags || [],
      camps: campsData?.camps || null,
      allExtras: allExtrasData?.extras || null,
      allEvents: allEventsData?.events || null,
    },
  };
}

export default Stage;
